import { useCallback, useEffect } from 'react';

import { TAB } from '~/constants/Tab';

import { type UncefactUnitType } from '~/types/unit';

import { useQueryCustomFields } from '~/data/customField';
import { getSearchBody } from '~/data/deliveryNote';
import { useQueryUserData } from '~/data/user';

import {
  setDashboard_individualDateRange,
  setDashboard_predefinedDateRange,
  setDashboard_selectedAcceptState,
  setDashboard_selectedArticle,
  setDashboard_selectedArticleNumber,
  setDashboard_selectedCategory,
  setDashboard_selectedCostCenter,
  setDashboard_selectedCustomerNumber,
  setDashboard_selectedCustomFields,
  setDashboard_selectedDateRange,
  setDashboard_selectedFromSite,
  setDashboard_selectedOuId,
  setDashboard_selectedPermittedCostCenters,
  setDashboard_selectedPermittedToSites,
  setDashboard_selectedProcessState,
  setDashboard_selectedRecipient,
  setDashboard_selectedSettledStatus,
  setDashboard_selectedSupplier,
  setDashboard_selectedToSiteRecipient,
  setDashboard_selectedToSiteSupplier,
  setDashboard_selectedToSiteSupplierTradeContact,
  setDashboard_selectedUnit,
  setDashboard_tab,
} from '~/redux/filtersSlice';
import { useDispatch, useSelector } from '~/redux/store';

import { FilterGroupFilter } from '~/models/filters/FilterGroupFilter';

import { clone } from '@vestigas/utils';
import { dateUtils } from '~/utils/dateUtils';
import { getDatesForPredefinedDateRange } from '~/utils/filters';
import { LOG, log } from '~/utils/logging';

import { getAggregationFromDateRange, selectDashboardFilters } from './utils';

export const useDashboard = () => {
  const dispatch = useDispatch();

  const {
    filterGroups,
    individualDateRange,
    selectedAcceptState,
    selectedArticle,
    selectedArticleNumber,
    selectedCategory,
    selectedCostCenter,
    selectedCostCenters,
    selectedCustomerNumber,
    selectedCustomFields,
    selectedDateRange,
    selectedFilterGroup,
    selectedFromSite,
    selectedOuId,
    selectedPermittedCostCenters,
    selectedPermittedToSites,
    selectedPredefinedDateRange,
    selectedProcessState,
    selectedRecipient,
    selectedSettledStatus,
    selectedSites,
    selectedSupplier,
    selectedTab,
    selectedToSiteRecipient,
    selectedToSiteSupplier,
    selectedToSiteSupplierTradeContact,
    selectedUnit,
  } = useSelector(selectDashboardFilters);

  const { data: customFields } = useQueryCustomFields();

  const aggregationInterval = getAggregationFromDateRange(selectedDateRange);

  const searchBody = getSearchBody({
    definedCustomFields: customFields,
    filterGroups: {
      acceptStates: selectedAcceptState,
      article: selectedArticle,
      articleNumber: selectedArticleNumber,
      category: selectedCategory,
      customerNumber: selectedCustomerNumber,
      dateRange: selectedDateRange,
      fromSite: selectedFromSite,
      ouId: selectedOuId,
      permittedCostCenters: selectedPermittedCostCenters,
      permittedToSites: selectedPermittedToSites,
      processStates: selectedProcessState,
      recipients: selectedRecipient,
      selectedCategory,
      selectedCostCenter,
      selectedCostCenters,
      selectedCustomFields,
      selectedOuId,
      selectedSites,
      settledStatus: selectedSettledStatus,
      suppliers: selectedSupplier,
      toSiteRecipient: selectedToSiteRecipient,
      toSiteSupplier: selectedToSiteSupplier,
    },
  });

  const { data: currentUser } = useQueryUserData(true);
  const featureFlags = currentUser?.featureFlags ?? {};

  const handleChangeFilterGroup = () => {
    const filterGroup = filterGroups.find(
      ({ id }) => id === selectedFilterGroup,
    );

    if (!filterGroup) {
      return;
    }

    dispatch(
      setDashboard_selectedAcceptState(
        filterGroup.filters.selectedAcceptState ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedArticle(filterGroup.filters.selectedArticle ?? []),
    );
    dispatch(
      setDashboard_selectedArticleNumber(
        filterGroup.filters.selectedArticleNumber ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedCategory(filterGroup.filters.selectedCategory ?? []),
    );
    dispatch(
      setDashboard_selectedCostCenter(
        filterGroup.filters.selectedCostCenter ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedCustomerNumber(
        filterGroup.filters.selectedCustomerNumber ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedCustomFields(
        filterGroup.filters.selectedCustomFields ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedFromSite(filterGroup.filters.selectedFromSite ?? []),
    );
    dispatch(setDashboard_selectedOuId(filterGroup.filters.selectedOuId ?? []));
    dispatch(
      setDashboard_selectedPermittedCostCenters(
        filterGroup.filters.selectedPermittedCostCenters ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedPermittedToSites(
        filterGroup.filters.selectedPermittedToSites ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedProcessState(
        filterGroup.filters.selectedProcessState ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedRecipient(
        filterGroup.filters.selectedRecipient ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedSettledStatus(
        filterGroup.filters.selectedSettledStatus ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedSupplier(filterGroup.filters.selectedSupplier ?? []),
    );
    dispatch(
      setDashboard_selectedToSiteRecipient(
        filterGroup.filters.selectedToSiteRecipient ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedToSiteSupplier(
        filterGroup.filters.selectedToSiteSupplier ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedToSiteSupplierTradeContact(
        filterGroup.filters.selectedToSiteSupplierTradeContact ?? [],
      ),
    );
  };

  // Callback from FilterGroups so that Dashboard state selectedToSiteSupplier, etc. are up-to-date, data is filtered and chart is updated accordingly
  const handleFilterChange = (type, customField, filterValue) => {
    switch (type) {
      case FilterGroupFilter.FILTER.SELECTED_ACCEPT_STATE: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedAcceptState,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent('Filter accept state', LOG.FEATURE.DASHBOARD);

        dispatch(setDashboard_selectedAcceptState(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_ARTICLE: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedArticle,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent('Filter article', LOG.FEATURE.DASHBOARD);

        dispatch(setDashboard_selectedArticle(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_ARTICLE_NUMBER: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedArticleNumber,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter article number',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedArticleNumber(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_CATEGORY: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedCategory,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent('Filter category', LOG.FEATURE.DASHBOARD);

        dispatch(setDashboard_selectedCategory(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_COST_CENTER: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedCostCenter,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent('Filter cost center', LOG.FEATURE.DASHBOARD);

        dispatch(setDashboard_selectedCostCenter(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_CUSTOMER_NUMBER: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedCustomerNumber,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter customer number',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedCustomerNumber(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_FROM_SITE: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedFromSite,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent('Filter fromSite', LOG.FEATURE.DASHBOARD);

        dispatch(setDashboard_selectedFromSite(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_OU_ID: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedOuId,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter Organizational Unit ID',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedOuId(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_PERMITTED_COST_CENTERS: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedPermittedCostCenters,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter permittedCostCenters',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedPermittedCostCenters(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_PERMITTED_TO_SITES: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedPermittedToSites,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter permittedToSites',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedPermittedToSites(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_PROCESS_STATE: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedProcessState,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter process state',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedProcessState(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_RECIPIENT: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedRecipient,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent('Filter recipient', LOG.FEATURE.DASHBOARD);

        dispatch(setDashboard_selectedRecipient(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_SETTLED_STATUS: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedSettledStatus,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter settled status',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedSettledStatus(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_SUPPLIER: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedSupplier,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent('Filter supplier', LOG.FEATURE.DASHBOARD);

        dispatch(setDashboard_selectedSupplier(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_TO_SITE_RECIPIENT: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedToSiteRecipient,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter toSiteRecipient',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedToSiteRecipient(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_TO_SITE_SUPPLIER: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedToSiteSupplier,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter toSiteSupplier',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedToSiteSupplier(filterValue));

        break;
      }

      case FilterGroupFilter.FILTER.SELECTED_TO_SITE_SUPPLIER_TRADE_CONTACT: {
        log.info({
          message: `Change filter value of ${type}`,
          data: {
            from: selectedToSiteSupplierTradeContact,
            to: filterValue,
          },
          breadcrumb: {
            category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
            event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
            message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
            type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
          },
        });
        log.productAnalyticsEvent(
          'Filter toSiteSupplierTradeContact',
          LOG.FEATURE.DASHBOARD,
        );

        dispatch(setDashboard_selectedToSiteSupplierTradeContact(filterValue));

        break;
      }

      default: {
        break;
      }
    }

    const newSelectedCustomFields = clone(selectedCustomFields);

    if (customField) {
      const selectedCustomFieldIndex = selectedCustomFields.findIndex(
        ({ key }) => key === customField.key,
      );

      log.info({
        message: `Change filter value of ${customField.key}`,
        data: {
          from: selectedCustomFields[selectedCustomFieldIndex]?.filterValue,
          to: filterValue,
        },
        breadcrumb: {
          category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
          event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
          message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
          type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
        },
      });
      log.productAnalyticsEvent('Filter custom field', LOG.FEATURE.DASHBOARD);

      if (selectedCustomFieldIndex === -1) {
        // If the custom field has not filter applied yet, push it to the selectedCustomFields filter.
        newSelectedCustomFields.push({
          filterValue,
          key: customField.key,
        });
      } else if (filterValue.length > 0) {
        // If a filter value has been added, overwrite the custom field filter
        newSelectedCustomFields[selectedCustomFieldIndex].filterValue =
          filterValue;
      } else {
        // If all filter values have been removed, remove the whole custom field entry from the selectedCustomFields variable
        // because we don't want empty custom field entries in selectedCustomFields.
        newSelectedCustomFields.splice(selectedCustomFieldIndex, 1);
      }

      dispatch(setDashboard_selectedCustomFields(newSelectedCustomFields));
    }
  };

  const handleDateRangeChange = (value) => {
    const numberOfDays = dateUtils.getNumberOfDays(value[0], value[1]);
    if (numberOfDays >= 0 && numberOfDays <= 3650) {
      log.info({
        message: 'Change filter value of selected date range',
        data: {
          from: selectedDateRange,
          to: value,
        },
        breadcrumb: {
          category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
          event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
          message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
          type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
        },
      });
      log.productAnalyticsEvent('Filter date range', LOG.FEATURE.DASHBOARD);

      dispatch(setDashboard_selectedDateRange(value));
      dispatch(setDashboard_individualDateRange(true));
      dispatch(setDashboard_predefinedDateRange('individual')); // Reset predefined date range
    }
  };

  const handlePredefinedDateRangeChange = (value) => {
    log.info({
      message: 'Change filter value of selected predefined date range',
      data: {
        from: selectedPredefinedDateRange,
        to: value,
      },
      breadcrumb: {
        category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
        event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
        message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
        type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
      },
    });
    log.productAnalyticsEvent(
      `Filter predefined date range: ${value}`,
      LOG.FEATURE.DASHBOARD,
    );

    dispatch(setDashboard_predefinedDateRange(value));
    dispatch(
      setDashboard_selectedDateRange(getDatesForPredefinedDateRange(value)),
    );
    dispatch(setDashboard_individualDateRange(false));
  };

  const handleUnitChange = useCallback(
    (unit: UncefactUnitType | undefined) => {
      log.info({
        message: 'Change filter value of unit',
        data: {
          from: selectedUnit,
          to: unit,
        },
        breadcrumb: {
          category: LOG.BREADCRUMB.FILTER_CHANGE.CATEGORY,
          event_id: LOG.BREADCRUMB.FILTER_CHANGE.KEY,
          message: LOG.BREADCRUMB.FILTER_CHANGE.DEFAULT_MESSAGE,
          type: LOG.BREADCRUMB.FILTER_CHANGE.TYPE,
        },
      });
      log.productAnalyticsEvent('Filter unit', LOG.FEATURE.DASHBOARD);

      dispatch(setDashboard_selectedUnit(unit));
    },
    [dispatch, selectedUnit],
  );

  const handleChangeTab = (_event, value) => {
    if (value === TAB.DASHBOARD.CHARTS.INDEX) {
      log.productAnalyticsEvent('Open charts tab', LOG.FEATURE.DASHBOARD);
    }

    if (value === TAB.DASHBOARD.REPORT.INDEX) {
      log.productAnalyticsEvent('Open reports tab', LOG.FEATURE.DASHBOARD);
    }

    dispatch(setDashboard_tab(value));
  };

  useEffect(() => {
    if (selectedFilterGroup) {
      handleChangeFilterGroup();
    }
  }, [selectedFilterGroup]);

  // Make sure a unit is selected in the chart tab.
  useEffect(() => {
    if (selectedTab === TAB.DASHBOARD.CHARTS.INDEX && !selectedUnit) {
      dispatch(setDashboard_selectedUnit('EA'));
    }
  }, [selectedTab, selectedUnit, dispatch]);

  return {
    aggregationInterval,
    featureFlags,
    handleChangeTab,
    handleDateRangeChange,
    handleFilterChange,
    handlePredefinedDateRangeChange,
    handleUnitChange,
    individualDateRange,
    searchBody,
    selectedDateRange,
    selectedPredefinedDateRange,
    selectedTab,
    selectedUnit,
  };
};
